import { useTranslation } from "@next-platform/client-common-core";
import useShouldBlurNsfwCover from "@next-platform/client-common-core/rulebook/cards/useShouldBlurNsfwCover";
import { type DiscoveredRulebook, type IRulebookHit, mapRulebookHit } from "@next-platform/client-common-core/rulebook/picker/rulebookDiscoverHit";
import { RulebookSelectionSort } from "@next-platform/client-common-core/rulebook/picker/rulebookSelection";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconVariants,
  Input,
  ListEmpty,
  Spinner,
  Text,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";
import type { RulebookMeterChip } from "@next-platform/packages-ts-types";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Configure, InstantSearch, useInfiniteHits, useInstantSearch, useSearchBox, useStats } from "react-instantsearch";

import Trans from "@/components/Trans/Trans";
import RulebookListCard from "@/pages/RulebookMy/RulebookListCard";
import { AvatarPreset } from "@/types";
import { getAvatarUrl } from "@/utils/getAvatarUrl";

import useRulebookDiscoverSearch from "./useRulebookDiscoverSearch";

const EMPTY_METER_CHIPS: RulebookMeterChip[] = [];

type RulebookDiscoverTabProps = {
  apiKey: string;
  collectionName: string;
  sort: RulebookSelectionSort;
  pickedId: string | null;
  /** The current pick's card, kept listed when a search or sort would drop it (as My Rulebooks keeps its pick). */
  picked?: DiscoveredRulebook;
  onPick: (rulebook: DiscoveredRulebook) => void;
  /** Tab switch and sort, shared with the My Rulebooks tab. */
  renderControls: () => React.ReactNode;
  /** Root test id; child ids hang off it as `RulebookDiscoverTab-Search` etc. */
  dataTestId?: string;
};

type DiscoverResultsProps = Pick<RulebookDiscoverTabProps, "pickedId" | "picked" | "onPick" | "renderControls" | "dataTestId">;

type DiscoverRulebookListItemProps = {
  rulebook: DiscoveredRulebook;
  isSelected: boolean;
  shouldBlurNsfw: boolean;
  onPick: (rulebook: DiscoveredRulebook) => void;
  dataTestId: string;
};

const DiscoverRulebookListItem = memo(({ rulebook, isSelected, shouldBlurNsfw, onPick, dataTestId }: DiscoverRulebookListItemProps) => {
  const handleSelect = useCallback(() => onPick(rulebook), [onPick, rulebook]);

  return (
    <RulebookListCard
      id={rulebook.id}
      name={rulebook.name}
      description={rulebook.description}
      coverUrl={getAvatarUrl(rulebook.cover_url ?? "", { preset: AvatarPreset.IMAGE_500X650 }) ?? undefined}
      isCoverBlurred={Boolean(shouldBlurNsfw && rulebook.isCoverNsfw)}
      meterChips={rulebook.meter_chips ?? EMPTY_METER_CHIPS}
      numHiddenMeters={rulebook.num_hidden_meters ?? 0}
      numRules={rulebook.num_rules ?? 0}
      numBots={rulebook.num_attached_characters ?? 0}
      numPersonas={rulebook.num_attached_personas ?? 0}
      isDefinitionVisible={rulebook.definition_visible}
      mode="public"
      creatorUsername={rulebook.creatorUsername}
      isSelected={isSelected}
      onOpen={handleSelect}
      data-testid={dataTestId}
    />
  );
});

const DiscoverHits = ({ pickedId, picked, onPick, dataTestId }: Pick<DiscoverResultsProps, "pickedId" | "picked" | "onPick" | "dataTestId">) => {
  const { t } = useTranslation();
  const { items, isLastPage, showMore } = useInfiniteHits<IRulebookHit>();
  const { status, error, refresh } = useInstantSearch();
  // The pick stays on top even when the current query or sort doesn't return it, so confirm never acts on a hidden card.
  const rulebooks = useMemo(() => {
    const hits = items.map(mapRulebookHit);
    return picked && picked.id === pickedId && !hits.some((hit) => hit.id === picked.id) ? [picked, ...hits] : hits;
  }, [items, picked, pickedId]);
  const shouldBlurNsfw = useShouldBlurNsfwCover();
  const rulebookCards = useMemo(
    () =>
      rulebooks.map((rulebook) => (
        <DiscoverRulebookListItem
          key={rulebook.id}
          rulebook={rulebook}
          shouldBlurNsfw={shouldBlurNsfw}
          isSelected={rulebook.id === pickedId}
          onPick={onPick}
          dataTestId={`${dataTestId}-Card-${rulebook.id}`}
        />
      )),
    [rulebooks, shouldBlurNsfw, pickedId, onPick, dataTestId],
  );

  const hasError = status === "error" || Boolean(error);
  if (!rulebooks.length && !hasError) {
    // An empty page mid-request isn't "no results" yet.
    return status === "loading" || status === "stalled" ? (
      <Spinner className="mx-auto" />
    ) : (
      <div data-testid={`${dataTestId}-Empty`}>
        <ListEmpty
          isLoading={false}
          title={t("common:noResultsFound")}
          description={t("rulebookEditor:attach.discover.noResults", "No public Rulebook matches your search.")}
        />
      </div>
    );
  }
  // A failed search shows the error even while stale hits or a retained pick are listed below it.
  return (
    <>
      {hasError ? (
        <div className="flex flex-col items-center gap-2" data-testid={`${dataTestId}-Error`}>
          <ListEmpty
            isLoading={false}
            title={t("rulebookEditor:attach.discover.searchError", "Couldn't load public Rulebooks.")}
            description={t("rulebookEditor:attach.discover.searchErrorHint", "Please try again.")}
          />
          <Button
            type="button"
            variant={ButtonVariant.OUTLINED}
            size={ButtonSize.MEDIUM}
            className="self-center"
            onClick={refresh}
            data-testid={`${dataTestId}-Retry`}
          >
            <Trans i18nKey="common:retry" defaults="Retry" />
          </Button>
        </div>
      ) : null}
      {rulebookCards}
      {isLastPage || hasError ? null : (
        <Button
          type="button"
          variant={ButtonVariant.OUTLINED}
          size={ButtonSize.MEDIUM}
          className="self-center"
          onClick={showMore}
          data-testid={`${dataTestId}-LoadMore`}
        >
          <Trans i18nKey="common:loadMore" defaults="Load more" />
        </Button>
      )}
    </>
  );
};

/** Search box, result count and shared controls, bound to the InstantSearch state. */
const DiscoverResults = ({ pickedId, picked, onPick, renderControls, dataTestId }: DiscoverResultsProps) => {
  const { t } = useTranslation();
  const { refine } = useSearchBox();
  const { nbHits } = useStats();
  const [stateQuery, setStateQuery] = useState("");
  const countValues = useMemo(() => ({ count: nbHits }), [nbHits]);
  const handleQuery = useCallback((value?: string) => setStateQuery(value ?? ""), []);

  useEffect(() => {
    refine(stateQuery);
  }, [stateQuery, refine]);

  return (
    <div className="flex flex-col gap-4" data-testid={`${dataTestId}-Results`}>
      <Input
        placeholder={t("rulebookEditor:attach.discover.search", "Search public Rulebooks")}
        iconStart={IconVariants.SEARCH}
        value={stateQuery}
        onChange={handleQuery}
        data-testid={`${dataTestId}-Search`}
      />
      <div className="flex items-center justify-between">
        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-10">
          <Trans i18nKey="common:results.found" values={countValues} />
        </Text>
        <div className="flex items-center gap-2">{renderControls()}</div>
      </div>
      <div className="flex min-h-[320px] flex-col gap-md">
        <DiscoverHits pickedId={pickedId} picked={picked} onPick={onPick} dataTestId={dataTestId} />
      </div>
    </div>
  );
};

/**
 * Discover tab of the Rulebook picker: approved public Rulebooks from the Typesense rulebooks index, with the same
 * app, visibility and NSFW filters as the Lorebook picker's Discover tab.
 */
const RulebookDiscoverTab = ({
  apiKey,
  collectionName,
  sort,
  pickedId,
  picked,
  onPick,
  renderControls,
  dataTestId = "RulebookDiscoverTab",
}: RulebookDiscoverTabProps) => {
  const { adapter, indexName, configureProps, shouldDelayQuery } = useRulebookDiscoverSearch({ apiKey, collectionName, sort });

  return (
    <div data-testid={dataTestId}>
      {shouldDelayQuery || !adapter?.searchClient ? (
        // Keep the tab switch reachable while the search waits (e.g. for the user's settings).
        <div className="flex flex-col gap-4" data-testid={`${dataTestId}-Waiting`}>
          <div className="flex items-center justify-end gap-2">{renderControls()}</div>
          <Spinner className="mx-auto" />
        </div>
      ) : (
        <InstantSearch searchClient={adapter.searchClient} indexName={indexName}>
          <Configure {...configureProps} />
          <DiscoverResults pickedId={pickedId} picked={picked} onPick={onPick} renderControls={renderControls} dataTestId={dataTestId} />
        </InstantSearch>
      )}
    </div>
  );
};

export default memo(RulebookDiscoverTab);
